import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { ApiProvider } from "@src/store/ApiProvider";
import { getInitialState } from "@src/store/helpers";

export const MISSING_INFORMATION_WINDOW_DAYS = 365;

/* Deliberately a dedicated axios instance rather than the shared default one.
 * The global interceptor in src/utils/axiosInterceptor.ts turns any 401 into an
 * auth:session-expired event, which logs the user out of the Portal. AutoPO is a
 * separate service, so a 401 from it must never end the Portal session. */
const autoPoClient = axios.create({ timeout: 10000 });

interface IStatisticsEnvelope {
	data?: {
		missingInformationCount?: number;
	};
}

export const getMissingInformationCount = createAsyncThunk<number>(
	"autoPo/getMissingInformationCount",
	async (_arg, { getState }) => {
		const baseUrl = ApiProvider.default.autoPoBaseURL;
		if (!baseUrl) return 0;

		const token = (getState() as { settings?: { token?: string } })?.settings?.token ?? "";
		if (!token) return 0;

		const url = `${baseUrl.replace(/\/$/, "")}/PurchaseOrders/statistics?days=${MISSING_INFORMATION_WINDOW_DAYS}`;
		const response = await autoPoClient.get<IStatisticsEnvelope>(url, {
			headers: { Authorization: `Bearer ${token}` },
		});

		return response.data?.data?.missingInformationCount ?? 0;
	},
);

export const missingInformationCountSlice = createSlice({
	name: "missingInformationCount",
	initialState: getInitialState(0),
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(getMissingInformationCount.pending, (state) => {
				state.loading = true;
			})
			.addCase(getMissingInformationCount.fulfilled, (state, action) => {
				state.loading = false;
				state.init = true;
				state.isInitialLoad = false;
				state.error = null;
				state.data = action.payload;
			})
			.addCase(getMissingInformationCount.rejected, (state, action) => {
				state.loading = false;
				state.init = true;
				state.isInitialLoad = false;
				state.error = action.error.message ?? null;
				state.data = 0;
			});
	},
});
